import { CodeType } from '../../../../entities/shared/code.type';
import { JobSection, JobSectionVerb } from '../../../modules/job.section.rights';
import { PortalContractorSectionRightIds } from '../../../modules/portal.contractor.section.rights';
import { SecurityRoleTiny } from '../../../security.role';
import { SecurityRoleUtility } from '../../../utility/security.roles.utility';
import { StageItemSecurityCapabilities } from '../stage/item/stage.item.security.capabilities';
import { JobSectionRights, itemLineSection, sectionRefusalMessage } from './job.section.policy';

/**
 * The one reader of the portal rights 61012–61023; the portal screens and the portal server both decide from it.
 *
 * Each verb is read on its own: a write never stands in for View, and a System Administrator gets no
 * exception, because the role holds no portal right and the server never bypasses one.
 */
export function getPortalContractorSectionRights(role: SecurityRoleTiny | null | undefined): JobSectionRights {

    const has = (right: number) => !!role && SecurityRoleUtility.checkTinyRights(role, right);
    const section = (ids: Record<JobSectionVerb, number>) => {

        const view = has(ids.view);

        return {
            view,
            create: view && has(ids.create),
            edit: view && has(ids.edit),
            delete: view && has(ids.delete),
        };
    };

    return {
        tasks: section(PortalContractorSectionRightIds.tasks),
        items: section(PortalContractorSectionRightIds.items),
        sors: section(PortalContractorSectionRightIds.sors),
    };
}

/** The line kinds a portal list or export may show; the input to `exportLines`. */
export function portalViewSections(rights: JobSectionRights): Record<'items' | 'sors', boolean> {

    return { items: rights.items.view, sors: rights.sors.view };
}

/** The refusal for a portal read or write, or null when the role allows it; a missing View is named before the verb. */
export function portalSectionRefusal(rights: JobSectionRights, section: JobSection, verb: JobSectionVerb): string | null {

    if (!rights[section].view) return sectionRefusalMessage(section, 'view');

    return rights[section][verb] ? null : sectionRefusalMessage(section, verb);
}

/** A line's section is its type (SOR or not), never the board the request came from. */
export function portalLineRefusal(rights: JobSectionRights, line: { type?: CodeType }, verb: JobSectionVerb): string | null {

    return portalSectionRefusal(rights, itemLineSection(line), verb);
}

/**
 * The line's stamped capabilities ANDed with its section's rights, so every menu, pen and bulk bar that already
 * reads the stamp follows the role too. The shared resolver stays role-free: the Field App stamps with it as well.
 */
export function withPortalSectionCapabilities(capabilities: StageItemSecurityCapabilities, line: { type?: CodeType }, rights: JobSectionRights): StageItemSecurityCapabilities {

    const section = rights[itemLineSection(line)];
    const canUpdate = capabilities.canUpdate && section.edit;
    const sectionName = itemLineSection(line) === 'sors' ? 'SORs' : 'Items';
    const roleRefusal = capabilities.canUpdate && !section.edit ? `Your role can't edit ${sectionName}` : undefined;
    const roleDeleteRefusal = capabilities.canDelete && !section.delete ? `Your role can't delete ${sectionName}` : undefined;

    return {
        ...capabilities,
        ...(roleRefusal ? { updateRefusal: roleRefusal } : {}),
        ...(roleDeleteRefusal ? { deleteRefusal: roleDeleteRefusal } : {}),
        canCreate: capabilities.canCreate && section.create,
        canUpdate,
        canUpdateNote: capabilities.canUpdateNote && section.edit,
        canDelete: capabilities.canDelete && section.delete,
    };
}
